/*eslint react-hooks/exhaustive-deps: "off"*/
import React, {useCallback, useEffect, useState} from 'react';
import {WELL_KNOWN_POSITION} from "../../Constants";
import useMapProps from "./useMapProps";
import MapBase from "./MapBase";
import {MapPropsProvider} from "./MapPropsContext";
import WidgetVisibleRadius from "./widgets/WidgetVisibleRadius";
import WidgetSearchByUid from "./widgets/WidgetSearchByUid";
import WidgetContainer from "./widgets/WidgetContainer";
import WidgetSearchByLonLatitude from "./widgets/WidgetSearchByLonLatitude";
import WidgetIconAtCommonUse, {OPTIONS as COMMON_USE_OPTIONS} from "./widgets/WidgetIconAtCommonUse";
import {
  formatServiceCode,
  formatUid,
  getApiUrl,
  getReadableFreq,
  makeTableSorterFn,
  positionToDegress
} from "../../utils";
import WidgetTable from "./widgets/WidgetTable";
import WidgetGeocoding from "./widgets/WidgetGeocoding";
import Seperator from "./widgets/Seperator";
import WidgetTableClicked from "./widgets/WidgetTableClicked";
import {getDistance} from "geolib";
import WidgetToolbar from "./widgets/WidgetToolbar";
import WidgetVworldCityTypeForCenter from "./widgets/WidgetVworldCityTypeForCenter";
import AlignRightText from "./widgets/AlignRightText";
import WidgetLegend from "./widgets/WidgetLegend";
import WidgetUidWithShortcut from "./widgets/WidgetUidWithShortcut";
import {message} from "antd";
import useVworldCityType from "./useVworldCityType";

const INITIAL_VIEW_STATE = {
  ...WELL_KNOWN_POSITION.남산공원,
  zoom: 16,
};


const ICON_MAPPING = {
  'SK_2G': ['2g-sk-out', 'SK 2G'],
  'SK_3G': ['3g-sk-out', 'SK 3G'],
  'SK_4G': ['4g-sk-out', 'SK 4G'],
  'KT_3G': ['3g-kt-out', 'KT 3G'],
  'KT_4G': ['4g-kt-out', 'KT 4G'],
  'LG_2G': ['2g-lg-out', 'LG 2G'],
  'LG_4G': ['4g-lg-out', 'LG 4G'],
};

message.config({
  top: 250,
  duration: 15,
  maxCount: 1,
});

const infoByZoom = () => {
  message.info({
    content: <span style={{fontSize:15}}> 해당 zoom에서는 검색이 불가합니다. 확대해 주시기 바랍니다.</span>,
  });
};


const MapCommonUse = () => {
  const mapProps = useMapProps({
    INITIAL_VISIBLE_RADIUS: 500,
    INITIAL_VIEW_STATE,
    INITIAL_POINT_LAYER_PROPS: {
      getIconCode: ({icon_nm}) => {
        try {
          return ICON_MAPPING[icon_nm][0];
        } catch (e) {
          console.error("not found iconCode for", icon_nm);
          return null;
        }
      },
      getIconWidth: (feature, zoom) => {
        let iconWidth = 30;
        if(zoom <= 12 ){
          iconWidth -= iconWidth * (15 - Math.max(7, zoom)) / 11;
          return Math.min(25.2, iconWidth);
        } else if( zoom <= 13) {
          iconWidth -= iconWidth * (15 - Math.max(7, zoom)) / 5;
          return Math.min(25.2, iconWidth);
        } else if (zoom <= 14){
          iconWidth -= iconWidth * (15 - Math.max(7, zoom)) / 3;
          return Math.min(25.2, iconWidth);
        } else if ( zoom === 15) {
          iconWidth -= iconWidth * (16 - Math.max(7, zoom)) / 4;
          return Math.min(30, iconWidth);
        } else if ( zoom >= 16 ){
          return 30;
        }
      }
    },
    INITIAL_WIDGET_STATE: {
      icon_nm_list: COMMON_USE_OPTIONS.map(({ icon }) => icon),
      visible_radius: 500,
      search_latitude: false,
      search_longitude: false,
    },
  });

  const {
    viewState,
    setIsEnableDistanceToClickedPoint,
    clickedPointIconObjects,
    setClickedPointIconObjects,
    widgetState,
    setPointsLayerFilterFunc,
    setVisibleRadius,
    setNullForMapViewType,
    setSearchByUidForMapViewType,
    setIsClusteringPoint,
    setFilteredPointsResponseList,
  } = mapProps;

  const {latitude, longitude, zoom} = viewState;
  const [vworldCityType, setVworldCityType] = useState();

  useEffect(() => {
    if( zoom < 13) {
      infoByZoom();
    } else if( zoom >= 13) {
      message.destroy();
    }
    // zoom이 15이하일 때는 클러스터링 보기, 초과부터 아이콘 보기
    if(zoom <= 15){
      setIsClusteringPoint(true);
    } else if(zoom > 15){
      setIsClusteringPoint(false);
    }
  }, [zoom]);

  useEffect(() => {
    setIsEnableDistanceToClickedPoint(true);
  }, []);

  useEffect(() => {
    const {icon_nm_list, visible_radius} = widgetState;
    setPointsLayerFilterFunc(() => ({icon_nm}) => icon_nm_list.includes(icon_nm));
    setVisibleRadius(visible_radius);
  }, [widgetState]);

  const { setPosition: setPositionForCityType } = useVworldCityType(cityType => {
    setVworldCityType(cityType);
  });

  useEffect(() => {
    setPositionForCityType({ latitude, longitude });
  }, [clickedPointIconObjects]);

  const onClickedPointInWidgetTable = useCallback((feature) => {
    const {latitude, longitude, lat, lon} = feature;
    const newLatitude = positionToDegress(latitude || lat);
    const newLongitude = positionToDegress(longitude || lon);
    const newFeature = {...feature, position: [newLongitude, newLatitude]};
    setClickedPointIconObjects([newFeature]);
  }, [setClickedPointIconObjects]);

  // useEffect(() => {
  //   const clsNames = clickedPointIconObjects.map(({cls_code}) => {
  //    if(cls_code === '21') return '고정국';
  //    if(cls_code === '32') return '기지국';
  //    if(cls_code === '85') return '이동중계국';
  //    if(cls_code === '92') return '아마추어국';
  //   });
  //
  // }, [clickedPointIconObjects]);

  return (
    <MapPropsProvider value={mapProps}>
      <MapBase>
        <WidgetContainer
          onChange={tabTitle => {
            if ( tabTitle === '허가번호 검색' ) {
              setSearchByUidForMapViewType();
              setClickedPointIconObjects([]);
              setVisibleRadius(0);
            }
            else {
              const {visible_radius} = widgetState;
              setVisibleRadius(visible_radius);
              setClickedPointIconObjects([]);
              setFilteredPointsResponseList([]);
              setNullForMapViewType();
            }
          }}
        >
          <div title={"주소 검색"}>
            <WidgetGeocoding style={{
                margin: '-10px 0px 10px 0px',
            }}/>
            <WidgetVisibleRadius/>
            <Seperator/>
            <WidgetIconAtCommonUse/>
          </div>
          <div title={"위,경도 검색"}>
            <WidgetSearchByLonLatitude/>
          </div>
          <div title={"허가번호 검색"}>
            <WidgetSearchByUid fetchUrl={getApiUrl({type: "type3"})}/>
          </div>
        </WidgetContainer>

        <WidgetVworldCityTypeForCenter />

        <WidgetTableClicked
          fetchUrl={getApiUrl({type: "type2"})}
          getTitle={objects => `${objects.length}개의 무선국`}
          serviceInfo={true}
          objects={clickedPointIconObjects}
          getRowTitle={({uid, rds_svc_form_cd}) => `${formatUid(uid)} (${formatServiceCode(rds_svc_form_cd)})`}
          getRow={({uid, address, cus_fln}) =>
            <table>
              <tbody>
              <tr>
                <th>허가번호</th>
                <td>{formatUid(uid)}</td>
              </tr>
              <tr>
                <th>시설자명</th>
                <td>{cus_fln}</td>
              </tr>
              <tr>
                <th>설(상)치장소</th>
                <td>{address}</td>
              </tr>
              <tr>
                <th>용도지역정보</th>
                <td>{vworldCityType}</td>
              </tr>
              </tbody>
            </table>
          }
        />
{/*        허가번호, 상대거리DIST, 설치장소RDS_TRS_ADR, 장치번호RDS_EQM_SNU, 인증번호TOAP_NO, 경도LON1, 위도LAT1, 신목적사항RDS_PPS_MTR_NM,*/}
{/*주파수FRQ_HZ, 출력ARW_PWR_WTT, 안테나 설치형태ARW_NM, 이득(db)ARW_GAN_NMV, 해발고ARW_GND_ALTD_HET,*/}
{/*지상고ARW_ALT_ALTD_HET, 노출고ARW_EXPS_ALTD_HET*/}
        <WidgetTable
          fetchUrl={getApiUrl({type: "type1"})}
          getTitle={(layer) => `${layer.length}개의 무선국`}
          isUidSearch={true}
          columns={[
            {title: '순번', dataIndex: 'index', key: 'index', width: 75, align: 'center', sorter: makeTableSorterFn('index'), fixed: 'left'},
            {
              title: '허가번호', dataIndex: 'uid', key: 'uid', width: 180,
              render: (uid, feature) => WidgetUidWithShortcut({feature, onClick: onClickedPointInWidgetTable})
              , sorter: makeTableSorterFn('uid'), fixed: 'left'
            },
            {
              title: '상대거리(m)', dataIndex: 'dist', key: 'dist', width: 120,
              sorter: makeTableSorterFn('dist'),
              // spring 응답에서 LATITUDE/LONGITUDE 필드명으로 응답이 옵니다.
              render: (value, record) => {
                const latitude = positionToDegress(record.latitude || record.LATITUDE);
                const longitude = positionToDegress(record.longitude || record.LONGITUDE);
                return AlignRightText({ text: getDistance(viewState, {latitude, longitude}) });
              }
            },
            {title: '시설자명', dataIndex: 'cus_fln', key: 'cus_fln', width: 120, sorter: makeTableSorterFn('cus_fln')},
            {title: '설치장소', dataIndex: 'address', key: 'address', width: 350, sorter: makeTableSorterFn('address')},
            {title: '장치개수', dataIndex: 'rds_eqm_snu', key: 'rds_eqm_snu', width: 100,
              render: text => AlignRightText({ text }),
              sorter: makeTableSorterFn('rds_eqm_snu')},
            {title: '인증번호', dataIndex: 'toap_no', key: 'toap_no', width: 200, sorter: makeTableSorterFn('toap_no')},
            {title: '위도', dataIndex: 'latitude', key: 'latitude', width: 140,
              render: (latitude, record) => record.latitude || record.LATITUDE,
            },
            {title: '경도', dataIndex: 'longitude', key: 'longitude', width: 140,
              render: (longitude, record) => record.longitude || record.LONGITUDE,
            },
            {title: '용도', dataIndex: 'rds_pps_mtr_nm', key: 'rds_pps_mtr_nm',
              sorter: makeTableSorterFn('rds_pps_mtr_nm')},
            {title: '주파수', dataIndex: 'frq_hz', key: 'frq_hz', width: 140,
              render: frq_hz => AlignRightText({ text: getReadableFreq(frq_hz) }),
              sorter: makeTableSorterFn('frq_hz')
            },
            {title: '출력', dataIndex: 'arw_pwr_wtt', key: 'arw_pwr_wtt', width: 80,
              render: value => AlignRightText({ text: value, numberComma: true }),
              sorter: makeTableSorterFn('arw_pwr_wtt')},
            {title: '안테나 설치형태', dataIndex: 'arw_nm', key: 'arw_nm', width: 140,
              sorter: makeTableSorterFn('arw_nm')},
            {title: '이득(db)', dataIndex: 'arw_gan_nmv', key: 'arw_gan_nmv', width: 100,
              render: value => AlignRightText({ text: value, numberComma: true }),
              sorter: makeTableSorterFn('arw_gan_nmv')},
            {title: '해발고', dataIndex: 'arw_gnd_altd_het', key: 'arw_gnd_altd_het', width: 100,
              render: value => AlignRightText({ text: value, numberComma: true }),
              sorter: makeTableSorterFn('arw_gnd_altd_het')},
            {title: '지상고', dataIndex: 'arw_alt_altd_het', key: 'arw_alt_altd_het', width: 100,
              render: value => AlignRightText({ text: value, numberComma: true }),
              sorter: makeTableSorterFn('arw_alt_altd_het')},
            {title: '노출고', dataIndex: 'arw_exps_altd_het', key: 'arw_exps_altd_het', width: 100,
              render: value => AlignRightText({ text: value, numberComma: true }),
              sorter: makeTableSorterFn('arw_exps_altd_het')},
          ]}
          horizontalScroll={2400}
        />

        <WidgetLegend />
      </MapBase>
      <WidgetToolbar/>
    </MapPropsProvider>
  );
};

export default MapCommonUse;
